import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AzTagComponent } from '../az-tag/az-tag.component';
import { AzButtonComponent, ButtonVariant } from '../az-button/az-button.component';

export interface AzTableButtonConfig {
  label: string;
  icon?: string;
  variant?: ButtonVariant;
  callback: (row: unknown) => void;
}

export interface AzTableColumn {
  key: string; // Property name in data object
  label: string; // Display label in header
  width?: string; // Optional width (e.g., '200px', '20%', 'auto')
  minWidth?: string; // Optional min-width (e.g., '100px')
  maxWidth?: string; // Optional max-width (e.g., '300px')
  align?: 'left' | 'center' | 'right'; // Text alignment
  type?: 'text' | 'tag' | 'email' | 'button'; // Cell rendering variant; defaults to 'text'
  buttonConfig?: AzTableButtonConfig; // Required when type === 'button'
  transform?: 'lowercase' | 'uppercase' | 'capitalize' | 'none'; // Optional text transformation applied to cell value
}

export interface AzTableAction<T = unknown> {
  icon: string; // Material icon name
  label: string; // Tooltip/aria-label
  callback: (row: T) => void; // Action callback
}

@Component({
  selector: 'app-az-table',
  standalone: true,
  imports: [CommonModule, AzTagComponent, AzButtonComponent],
  templateUrl: './az-table.component.html',
  styleUrl: './az-table.component.scss',
})
export class AzTableComponent<T = unknown> {
  @Input() data: T[] = [];
  @Input() columns: AzTableColumn[] = [];
  @Input() actions: AzTableAction<T>[] = [];
  @Input() emptyMessage = 'No data available';
  @Input() clickable = false;

  get hasButtonColumns(): boolean {
    return this.columns.some((c) => c.type === 'button');
  }

  @Output() rowClick = new EventEmitter<T>();

  onRowClick(row: T) {
    this.rowClick.emit(row);
  }

  onActionClick(action: AzTableAction<T>, row: T, event: Event) {
    event.stopPropagation();
    action.callback(row);
  }

  onButtonColumnClick(column: AzTableColumn, row: T, event: Event) {
    event.stopPropagation();
    column.buttonConfig?.callback(row);
  }

  getCellValue(row: T, column: AzTableColumn): string {
    const value = (row as Record<string, unknown>)[column.key];
    if (value == null) return '--';
    const str = String(value);
    if (column.transform === 'lowercase') return str.toLowerCase();
    if (column.transform === 'uppercase') return str.toUpperCase();
    return str;
  }

  /** Returns the rowspan value for a cell, or null if no span is defined. */
  getRowSpan(row: T, column: AzTableColumn): number | null {
    const spans = (row as Record<string, unknown>)['_spans'] as Record<string, number> | undefined;
    const span = spans?.[column.key];
    return span != null && span > 1 ? span : null;
  }

  /** Returns true if the cell should be omitted because a prior row spans over it. */
  isCellHidden(row: T, column: AzTableColumn): boolean {
    const hidden = (row as Record<string, unknown>)['_hidden'] as
      | Record<string, boolean>
      | undefined;
    return hidden?.[column.key] === true;
  }

  getColumnTextTransform(column: AzTableColumn): string | null {
    if (!column.transform || column.transform === 'none') return null;
    return column.transform; // 'lowercase' | 'uppercase' | 'capitalize' map 1:1 to CSS values
  }

  getColumnWidth(column: AzTableColumn): string | null {
    // For auto width, return null to let table layout handle it
    if (column.width === 'auto') return null;
    return column.width || null;
  }

  getColumnMinWidth(column: AzTableColumn): string | null {
    return column.minWidth || null;
  }

  getColumnMaxWidth(column: AzTableColumn): string | null {
    return column.maxWidth || null;
  }

  getColumnAlign(column: AzTableColumn): string {
    return column.align || 'left';
  }
}
